import React, { Component, Fragment } from 'react';
import { change, destroy } from 'redux-form';
import { connect } from 'react-redux';
import { Grid } from 'semantic-ui-react';
import _ from 'lodash';

import { LoadingBase } from 'Components/';
import { Main, Filter } from 'Containers';
import { ImportIngredientsFieldEnum as Fields } from 'Common/FieldEnum';
import { IMPORT_TYPE_MAP } from 'Common/Constants';
import { IMPORT_INGREDIENT_FORM_NAME } from 'Common/FormEnum';
import { errorToast, createToast, toastType } from 'Common/ToastHelper';
import { ImportPreview } from 'Common/Components/ImportPreview';
import { SKIP } from 'Common/Components/ImportPreview/helpers/Constants';

import * as ImportIngActions from './state/actions';
import { ImportIngredientForm } from './components/ImportIngredientForm';
import * as ImportIngSelectors from './state/selectors';
import {
  IMPORT_INGREDIENT_TYPE_ID,
  REQUIRED_COLUMNS,
  REQUIRED_COLUMNS_TO_USE_ON_MENUS,
} from './helpers/Constants';

class ImportIngredients extends Component {
  static path = '/importingredients';

  componentDidMount() {
    const { getImportIngColumns, getSavedImportConfigs } = this.props;

    getImportIngColumns();
    getSavedImportConfigs();
  }

  componentDidUpdate(prevProps) {
    const { clearError } = this.props;

    if (errorToast(this.props, prevProps, 'fetchError')) {
      clearError();
    }
  }

  componentWillUnmount() {
    const { resetState, destroy } = this.props;

    resetState();
    destroy(IMPORT_INGREDIENT_FORM_NAME);
  }

  get mainProps() {
    return {
      toolbox: {
        shown: true,
        saveButtonProps: { content: 'Import' },
        formName: IMPORT_INGREDIENT_FORM_NAME,
      },
    };
  }

  hasAllRequiredColumns(selectedFieldList, canBeUsedOnMenus) {
    let hasAll = true;
    const missingColumns = [];
    const requiredColumns = canBeUsedOnMenus
      ? REQUIRED_COLUMNS_TO_USE_ON_MENUS
      : REQUIRED_COLUMNS;

    requiredColumns.forEach(col => {
      // Check if column has been selected
      if (!selectedFieldList.some(field => field[Fields.ColumnLabel] === col)) {
        hasAll = false;
        missingColumns.push(col);
      }
    });

    return {
      hasAll,
      missingColumns,
    };
  }

  importIngredients = async formValues => {
    const { importIngredients, importIngredientsAndSaveConfig } = this.props;

    if (!formValues[Fields.File]) {
      createToast(
        toastType.error,
        'Error',
        'A file is required to be uploaded.',
        true
      );
      return;
    }

    const selectedFieldList = formValues[Fields.SelectedFieldList];
    const { hasAll, missingColumns } = this.hasAllRequiredColumns(
      selectedFieldList,
      formValues[Fields.CanBeUsedOnMenus]
    );

    if (!hasAll) {
      let missingColumnsStr = '';
      if (missingColumns.length > 2) {
        missingColumnsStr = `${missingColumns
          .slice(0, missingColumns.length - 1)
          .join(', ')}, and ${missingColumns[missingColumns.length - 1]}`;
      } else if (missingColumns.length === 2) {
        missingColumnsStr = missingColumns.join(' and ');
      } else {
        missingColumnsStr = missingColumns.join('');
      }

      const missingMessage = `The following columns are required: ${missingColumnsStr}`;

      createToast(toastType.error, 'Error', missingMessage, true);
      return;
    }

    const formValuesCopy = _.pickBy(
      formValues,
      (value, key) =>
        !(key === Fields.ConfigName || key === Fields.LoadedConfigId)
    );
    const importType = formValues[Fields.ImportType];
    let imported = false;
    let toastMessage = 'Ingredient import has been queued.';

    if (importType === IMPORT_TYPE_MAP.ONE_TIME.value) {
      imported = await importIngredients(formValuesCopy);
    } else if (importType === IMPORT_TYPE_MAP.CREATE_NEW.value) {
      formValuesCopy[Fields.Name] = formValues[Fields.ConfigName];
      imported = await importIngredientsAndSaveConfig(formValuesCopy);
      toastMessage =
        'Import configurations have been saved and the ingredient import has been queued.';
    } else {
      imported = await importIngredients(formValuesCopy);
    }

    if (imported) {
      createToast(toastType.success, 'Success', toastMessage, true);
    }
  };

  updatePreviewData = (value, i) => {
    const { change, selectedFieldList } = this.props;

    if (
      value !== SKIP &&
      selectedFieldList.some(field => field[Fields.ColumnLabel] === value)
    ) {
      createToast(
        toastType.error,
        'Error',
        `You have already selected the field ${value}.`,
        true
      );
      return false;
    }

    const updatedSelectedFieldList = [...selectedFieldList];
    updatedSelectedFieldList[i] = {
      ...updatedSelectedFieldList[i],
      [Fields.ColumnLabel]: value,
      FieldName: value,
    };

    change(
      IMPORT_INGREDIENT_FORM_NAME,
      Fields.SelectedFieldList,
      updatedSelectedFieldList
    );

    return true;
  };

  render() {
    const {
      ingredientOptions,
      previewImportIngredient,
      fileData,
      fetching,
      selectedFieldList,
      clearColumnMapping,
      clearFileAndPreview,
      resetColumnMapping,
      hasHeaderInfo,
    } = this.props;

    if (fetching) {
      return <LoadingBase />;
    }

    return (
      <Fragment>
        <Filter title="Import Ingredients" />
        <Main {...this.mainProps}>
          <ImportIngredientForm
            initialValues={{
              [Fields.Delimiter]: ',',
              [Fields.IncludeHeaders]: true,
              [Fields.ImportType]: IMPORT_TYPE_MAP.ONE_TIME.value,
              [Fields.ImportTypeId]: IMPORT_INGREDIENT_TYPE_ID,
              [Fields.SelectedFieldList]: [],
              [Fields.CanBeUsedOnMenus]: false,
              [Fields.UpdateKey]: null,
            }}
            onSubmit={this.importIngredients}
            previewImportIngredient={previewImportIngredient}
            fileData={fileData}
          />
          {Array.isArray(fileData) && fileData.length > 0 && (
            <Grid>
              <Grid.Row>
                <Grid.Column>
                  <ImportPreview
                    options={ingredientOptions}
                    fileData={fileData}
                    header={selectedFieldList}
                    onUploadAnotherClick={clearFileAndPreview}
                    updatePreviewData={this.updatePreviewData}
                    resizable
                    useSelectPortal
                    onClearMappingClick={clearColumnMapping}
                    onResetClick={resetColumnMapping}
                    hasHeaderInfo={hasHeaderInfo}
                    tableColProps={{
                      minWidth: 175,
                    }}
                  />
                </Grid.Column>
              </Grid.Row>
            </Grid>
          )}
        </Main>
      </Fragment>
    );
  }
}

const mapStateToProps = state => ({
  fetching: ImportIngSelectors.getFetching(state),
  ingredientOptions: ImportIngSelectors.getIngredientOptions(state),
  fetchError: ImportIngSelectors.getFetchError(state),
  fileData: ImportIngSelectors.getFileDataForPreview(state),
  savedConfigOptionsMap: ImportIngSelectors.getSavedImportConfigsMap(state),
  selectedFieldList: ImportIngSelectors.getSelectedFieldList(state),
  hasHeaderInfo: ImportIngSelectors.getHasHeaderInfo(state),
});

const mapDispatchToProps = {
  getImportIngColumns: ImportIngActions.getImportIngColumns,
  importIngredients: ImportIngActions.importIngredients,
  previewImportIngredient: ImportIngActions.previewImportIngredient,
  clearError: ImportIngActions.clearError,
  clearFileAndPreview: ImportIngActions.clearFileAndPreview,
  getSavedImportConfigs: ImportIngActions.getSavedImportConfigs,
  importIngredientsAndSaveConfig:
    ImportIngActions.importIngredientsAndSaveConfig,
  clearColumnMapping: ImportIngActions.clearColumnMapping,
  change,
  resetColumnMapping: ImportIngActions.resetColumnMapping,
  resetState: ImportIngActions.resetState,
  destroy,
};

ImportIngredients = connect(
  mapStateToProps,
  mapDispatchToProps
)(ImportIngredients);

export { ImportIngredients };



// WEBPACK FOOTER //
// ./src/pages/importingredients/ImportIngredients.js